import Ajv, { ValidateFunction } from "ajv";
import addFormats from "ajv-formats";
import { YAMLException } from "js-yaml";
import isString from "lodash/isString";
import { parseYaml } from "utils/valueTransformers";
import { isEmail } from "validator";

// TODO (low) deprecated, refactor to remove
/* eslint-disable no-template-curly-in-string */
export const antdFormMessages = {
    required: "This field is required.",
    types: {
        email: "Please provide an e-mail address.",
        number: "Please enter a number.",
    },
    number: {
        range: "Please enter value between ${min} and ${max}",
    },
};
/* eslint-enable no-template-curly-in-string */

export type FinalFormValidator = (value: any, values: any) => undefined | string | any;

export const composeValidators =
    (...validators) =>
    (value) =>
        validators.reduce((error, validator) => error || validator(value), undefined);

export const requiredValidator: FinalFormValidator = (value) =>
    value === undefined || value === null || value === "" ? "Required." : undefined;

export const oneLineValidator: FinalFormValidator = (value) => {
    if (value && /\n/.test(value)) {
        return "No more than one line allowed";
    }
    return undefined;
};

export const emailValidator: FinalFormValidator = (value) =>
    isEmail(value) ? undefined : "Please enter an e-mail address.";
export const urlValidator = (value) => {
    // Allow unset URL. For required URL use
    // requiredValidator and urlValidator combined.
    if (!value) return undefined;
    try {
        /* eslint-disable no-new */
        new URL(value);
        return undefined;
    } catch (err) {
        return "Please enter a URL.";
    }
};

export const yamlValidator = (value) => {
    try {
        parseYaml(value);
        return undefined;
    } catch (error: any) {
        const typedError: YAMLException = error;
        const errorMessage = `${typedError?.name}: ${typedError?.reason}`;
        return errorMessage;
    }
};

export const minValue: (number) => FinalFormValidator = (min) => (value) => {
    if (value === "" || value === null || value === undefined) {
        return undefined;
    }
    if (typeof value !== "number" || Number.isNaN(value)) {
        return "Please enter a number.";
    }
    return value >= min ? undefined : `Should be at least ${min}.`;
};
export const numberValidator: FinalFormValidator = (value) => {
    if (value === "" || value === null || value === undefined) {
        return undefined;
    }
    return typeof value === "number" && !Number.isNaN(value)
        ? undefined
        : "Please enter a number.";
};
export const minLengthValidator: (number) => FinalFormValidator = (min) => (value) =>
    isString(value) && value.length >= min ? undefined : `Please enter at least ${min} characters.`;
export const maxLengthValidator: (number) => FinalFormValidator = (max) => (value) =>
    isString(value) && value.length <= max ? undefined : `Please enter at most ${max} characters.`;

// A single, shared Ajv instance is reused across all validations. Building a new
// Ajv + addFormats + compiling the whole schema is expensive, and this validator
// runs as a react-final-form field validator on every keystroke. See CU 86camf0px.
const sharedAjv = new Ajv({
    strict: false,
});
addFormats(sharedAjv as any);
// Be forgiving for `format: "time"` fields: ajv-formats' built-in "time" format
// requires seconds (HH:MM:SS), but the time picker (and users) commonly provide
// HH:MM. Accept both, keeping the with-utcoffset variants (HH:MM:SS+ZZ:ZZ / ...Z)
// valid — this regex is a superset of the built-in, so nothing previously valid
// becomes invalid.
sharedAjv.addFormat(
    "time",
    /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d(\.\d+)?)?(z|[+-]([01]\d|2[0-3])(:?[0-5]\d)?)?$/i,
);
sharedAjv.addKeyword({
    keyword: "smallest_unit",
    validate: function smallestUnitValidator(schema, data) {
        // Reset per invocation: the function is now module-level and shared, so its
        // errors array would otherwise accumulate across every validation call.
        smallestUnitValidator["errors"] = [];
        if (!Number.isInteger(data)) {
            smallestUnitValidator["errors"].push({
                keyword: "smallest_unit",
                message: "must be a whole number",
            });
            return false;
        }

        return true;
    },
    errors: true,
});

// Compiled validators are cached by schema identity. The schema object is stable
// across a form editing session (it comes from the record's input_schema), so we
// compile each schema exactly once instead of on every change.
const validatorCache = new WeakMap<object, ValidateFunction>();

const getCompiledValidator = (schema: any): ValidateFunction => {
    let validate = validatorCache.get(schema);
    if (validate == null) {
        const compiledSchema = {
            ...schema,
            description: schema.description ?? "",
        };
        // Guard against "$id already exists" when a different schema object reuses
        // an $id we've compiled before (Ajv keeps compiled schemas by $id).
        if (compiledSchema.$id) {
            sharedAjv.removeSchema(compiledSchema.$id);
        }
        validate = sharedAjv.compile(compiledSchema);
        validatorCache.set(schema, validate);
    }
    return validate;
};

export const validateValuesAgainstSchema = (value: { schema: any; values: any }) => {
    const validate = getCompiledValidator(value.schema);
    const isValid = validate(value.values);

    if (!isValid) {
        const error = validate.errors?.[0];
        // need to convert instancePath to an array to compare with dataPath in SchemaFormFragment
        const instancePath = error?.instancePath.split("/").filter((path, index) => index !== 0);
        const humanReadablePath = `data${error?.instancePath?.replace(/\//g, ".")}`;

        const validationError = {
            path: instancePath,
            message: error?.message ?? "unknown error",
            messageWithPath: `${humanReadablePath} ${error?.message ?? "unknown error"}`,
        };

        return validationError;
    }
};

export const schemaFormValidator = (value) => {
    if (value?.schema) {
        try {
            return validateValuesAgainstSchema(value);
        } catch (error) {
            console.warn("validate failed", error);
            return false;
        }
    }
};

export const MAX_LENGTH = 128;
